import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import EventScheduledReminderCard from '../../components/EventScheduledReminderCard';
import StickyActionHeader from '../../components/StickyActionHeader';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  BookmarkSimple,
  Check,
  Copy,
  Gift as GiftIcon,
  LinkSimple,
  LockSimple,
  MapPin,
  PencilSimple,
  Phone,
  Plus,
  Trash,
  Upload,
  UsersThree,
  X,
} from '@phosphor-icons/react';
import api from '../../services/api';
import { toast } from 'sonner';
import InfoTip from '../../components/InfoTip';
import useEventTypeConfig from '../../hooks/useEventTypeConfig';

const statusConfig = {
  available: { label: 'Available', color: 'bg-green-100 text-green-700' },
  reserved: { label: 'Reserved', color: 'bg-yellow-100 text-yellow-700' },
  purchased: { label: 'Purchased', color: 'bg-wedding-sage text-white' },
};

const emptyGift = { name: '', description: '', price: '', link: '' };
const emptyContribution = { amount: '', message: '' };
const emptyAddress = {
  recipient_name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'India',
  delivery_notes: '',
  is_visible_to_guests: false,
};

const errorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).filter(Boolean).join('. ') || fallback;
  return typeof detail === 'string' ? detail : fallback;
};

export default function GiftRegistry() {
  const { currentEvent, user } = useAuth();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const viewerRole = currentEvent?.viewer_role || user?.role;
  const canManageWishlist = ['manager', 'event_manager', 'bride', 'groom', 'host'].includes(viewerRole);
  const canUpdateGiftStatus = ['guest', 'admin_guest'].includes(viewerRole);
  const [gifts, setGifts] = useState([]);
  const [giftReveal, setGiftReveal] = useState({ event_ended: false, gifts: [] });
  const [deliveryAddress, setDeliveryAddress] = useState({ configured: false });
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [contributionGift, setContributionGift] = useState(null);
  const [editingGift, setEditingGift] = useState(null);
  const [finalizingAll, setFinalizingAll] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [newGift, setNewGift] = useState(emptyGift);
  const [contribution, setContribution] = useState(emptyContribution);
  const [addressForm, setAddressForm] = useState(emptyAddress);

  const fetchWishlist = useCallback(async () => {
    if (!currentEvent?.id) return;
    setLoading(true);
    try {
      const requests = [
        api.get(`/events/${currentEvent.id}/gifts`),
        api.get(`/events/${currentEvent.id}/gift-registry-settings`),
      ];
      if (canManageWishlist) {
        requests.push(api.get(`/events/${currentEvent.id}/gifts/reveal`));
      }
      const [giftResponse, addressResponse, revealResponse] = await Promise.all(requests);
      setGifts(giftResponse.data);
      setDeliveryAddress(addressResponse.data);
      setGiftReveal(revealResponse?.data || { event_ended: false, gifts: [] });
      if (addressResponse.data.configured) {
        setAddressForm({ ...emptyAddress, ...addressResponse.data });
      }
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to load the wishlist'));
    } finally {
      setLoading(false);
    }
  }, [canManageWishlist, currentEvent?.id]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const handleAddGift = async (event) => {
    event.preventDefault();
    try {
      await api.post(`/events/${currentEvent.id}/gifts`, {
        ...newGift,
        price: newGift.price ? parseFloat(newGift.price) : null,
      });
      toast.success(canManageWishlist ? 'Gift added as a draft' : 'Gift idea added to the wishlist');
      setShowAddDialog(false);
      setNewGift(emptyGift);
      fetchWishlist();
    } catch (error) {
      toast.error(errorMessage(error, canManageWishlist ? 'Failed to add gift' : 'Failed to send gift idea'));
    }
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post(`/events/${currentEvent.id}/gifts/bulk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(response.data.message);
      setShowBulkDialog(false);
      fetchWishlist();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to upload gifts'));
    }
  };

  const handleReserve = async (giftId) => {
    try {
      await api.put(`/events/${currentEvent.id}/gifts/${giftId}/reserve`);
      toast.success('Gift reserved');
      fetchWishlist();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to reserve gift'));
    }
  };

  const handleMarkPurchased = async (giftId) => {
    try {
      await api.put(`/events/${currentEvent.id}/gifts/${giftId}/purchase`);
      toast.success('Gift marked as purchased');
      fetchWishlist();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to update gift'));
    }
  };

  const openEditGift = (gift) => {
    if (gift.is_finalized) {
      toast.error('Finalized gifts cannot be edited');
      return;
    }
    setEditingGift({
      id: gift.id,
      name: gift.name || '',
      description: gift.description || '',
      price: gift.price ? String(gift.price) : '',
      link: gift.link || '',
    });
    setShowEditDialog(true);
  };

  const handleEditGift = async (event) => {
    event.preventDefault();
    if (!editingGift?.id) return;
    try {
      await api.put(`/events/${currentEvent.id}/gifts/${editingGift.id}`, {
        name: editingGift.name,
        description: editingGift.description,
        link: editingGift.link,
        price: editingGift.price ? parseFloat(editingGift.price) : null,
      });
      toast.success('Gift updated');
      setShowEditDialog(false);
      setEditingGift(null);
      fetchWishlist();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to update gift'));
    }
  };

  const handleDeleteGift = async (giftId) => {
    try {
      await api.delete(`/events/${currentEvent.id}/gifts/${giftId}`);
      toast.success('Gift deleted');
      fetchWishlist();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to delete gift'));
    }
  };

  const handleFinalizeGift = async (gift) => {
    try {
      const response = await api.put(`/events/${currentEvent.id}/gifts/${gift.id}/finalize`);
      toast.success(response.data.message || 'Gift finalized');
      fetchWishlist();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to finalize gift'));
    }
  };

  const handleFinalizeAllDrafts = async () => {
    setFinalizingAll(true);
    try {
      const response = await api.put(`/events/${currentEvent.id}/gifts/finalize-bulk`);
      toast.success(response.data.message || 'Draft gifts finalized');
      fetchWishlist();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to finalize draft gifts'));
    } finally {
      setFinalizingAll(false);
    }
  };

  const handleSaveAddress = async (event) => {
    event.preventDefault();
    setSavingAddress(true);
    try {
      const response = await api.put(`/events/${currentEvent.id}/gift-registry-settings`, addressForm);
      setDeliveryAddress(response.data);
      setShowAddressDialog(false);
      toast.success(
        response.data.is_visible_to_guests
          ? 'Delivery address saved and visible to guests'
          : 'Delivery address saved privately',
      );
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to save delivery address'));
    } finally {
      setSavingAddress(false);
    }
  };

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(addressText);
      toast.success('Delivery address copied');
    } catch (error) {
      toast.error('Could not copy the address');
    }
  };

  const handleRequestContribution = async (event) => {
    event.preventDefault();
    if (!contributionGift) return;
    try {
      await api.post(
        `/events/${currentEvent.id}/gifts/${contributionGift.id}/contribution-requests`,
        {
          amount: contribution.amount ? parseFloat(contribution.amount) : null,
          message: contribution.message || null,
        },
      );
      toast.success('Contribution request sent');
      setContributionGift(null);
      setContribution(emptyContribution);
      fetchWishlist();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to send contribution request'));
    }
  };

  const handleContributionDecision = async (requestId, accept) => {
    try {
      await api.put(`/events/${currentEvent.id}/gift-contribution-requests/${requestId}`, { accept });
      toast.success(accept ? 'Contribution request accepted' : 'Contribution request declined');
      fetchWishlist();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to respond to contribution request'));
    }
  };

  const visibleGifts = canManageWishlist
    ? gifts.filter((gift) => !gift.suggested_by)
    : gifts;
  const available = visibleGifts.filter((gift) => gift.is_finalized && gift.status === 'available').length;
  const reserved = visibleGifts.filter((gift) => gift.is_finalized && gift.status === 'reserved').length;
  const purchased = visibleGifts.filter((gift) => gift.is_finalized && gift.status === 'purchased').length;
  const pendingContributionCount = visibleGifts.reduce(
    (count, gift) => count + (gift.incoming_contribution_requests || []).filter((request) => request.status === 'pending').length,
    0,
  );
  const draftCount = visibleGifts.filter((gift) => !gift.is_finalized).length;
  const finalizedCount = visibleGifts.filter((gift) => gift.is_finalized).length;
  const displayGifts = canManageWishlist
    ? [...visibleGifts].sort((a, b) => Number(a.is_finalized) - Number(b.is_finalized))
    : visibleGifts;
  const showPrice = canUpdateGiftStatus || !giftReveal.event_ended;
  const addressText = useMemo(() => {
    if (!deliveryAddress.configured) return '';
    return [
      deliveryAddress.recipient_name,
      deliveryAddress.address_line1,
      deliveryAddress.address_line2,
      [deliveryAddress.city, deliveryAddress.state, deliveryAddress.postal_code].filter(Boolean).join(', '),
      deliveryAddress.country,
      deliveryAddress.delivery_notes ? `Delivery note: ${deliveryAddress.delivery_notes}` : null,
    ].filter(Boolean).join('\n');
  }, [deliveryAddress]);

  const renderGuestActions = (gift) => {
    const myRequest = gift.my_contribution_request;
    return (
      <div className="space-y-2">
        {gift.status === 'available' && (
          <Button variant="outline" size="sm" onClick={() => handleReserve(gift.id)} data-testid={`reserve-${gift.id}`}>
            <BookmarkSimple className="w-4 h-4 mr-1" /> Reserve
          </Button>
        )}
        {gift.status === 'reserved' && gift.is_reserved_by_current_user && (
          <Button
            variant="outline"
            size="sm"
            className="border-wedding-sage text-wedding-sage"
            onClick={() => handleMarkPurchased(gift.id)}
            data-testid={`purchase-${gift.id}`}
          >
            <Check className="w-4 h-4 mr-1" /> Mark Purchased
          </Button>
        )}
        {gift.status === 'purchased' && gift.is_purchased_by_current_user && (
          <span className="inline-flex items-center text-sm text-muted-foreground">
            <Check className="w-4 h-4 mr-1" /> Purchased by you
          </span>
        )}
        {['reserved', 'purchased'].includes(gift.status)
          && !gift.is_reserved_by_current_user
          && !gift.is_purchased_by_current_user && (
            <span className="block text-sm text-muted-foreground">
              {gift.status === 'reserved' ? 'Reserved' : 'Purchased'} by another guest
            </span>
          )}
        {gift.can_request_contribution && (
          <Button variant="outline" size="sm" onClick={() => setContributionGift(gift)}>
            <UsersThree className="w-4 h-4 mr-1" />
            {myRequest?.status === 'declined' ? 'Ask again' : 'Ask to contribute'}
          </Button>
        )}
        {myRequest?.status === 'pending' && (
          <p className="text-xs text-amber-700">Your contribution request is awaiting a response.</p>
        )}
        {myRequest?.status === 'declined' && (
          <p className="text-xs text-muted-foreground">Your contribution request was declined.</p>
        )}
        {myRequest?.status === 'accepted' && (
          <div className="rounded-md bg-green-50 p-2 text-xs text-green-800">
            <p className="font-medium">Contribution accepted by {myRequest.owner_name}</p>
            {myRequest.owner_phone && (
              <a className="mt-1 inline-flex items-center underline" href={`tel:${myRequest.owner_phone}`}>
                <Phone className="mr-1 h-3.5 w-3.5" /> {myRequest.owner_phone}
              </a>
            )}
          </div>
        )}
        {gift.incoming_contribution_requests?.map((request) => (
          <div key={request.id} className="min-w-56 rounded-md border p-2 text-xs">
            <p className="font-medium">{request.requester_name} wants to contribute</p>
            {request.amount && <p>Up to ₹{Number(request.amount).toFixed(2)}</p>}
            {request.message && <p className="mt-1 text-muted-foreground">“{request.message}”</p>}
            {request.status === 'pending' && (
              <div className="mt-2 flex gap-2">
                <Button size="sm" className="h-7 bg-wedding-sage hover:bg-wedding-sage/90" onClick={() => handleContributionDecision(request.id, true)}>
                  <Check className="mr-1 h-3.5 w-3.5" /> Accept
                </Button>
                <Button size="sm" variant="outline" className="h-7" onClick={() => handleContributionDecision(request.id, false)}>
                  <X className="mr-1 h-3.5 w-3.5" /> Decline
                </Button>
              </div>
            )}
            {request.status === 'accepted' && (
              <p className="mt-1 text-green-700">
                Accepted{request.requester_phone ? ` • ${request.requester_phone}` : ''}
              </p>
            )}
            {request.status === 'declined' && <p className="mt-1 text-muted-foreground">Declined</p>}
          </div>
        ))}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <StickyActionHeader>
          <div>
            <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="gifts-title">
              {eventTypeConfig.labels.registryTitle}
            </h1>
            <p className="text-muted-foreground">
              {canUpdateGiftStatus
                ? `${available} available • ${reserved} reserved • ${purchased} purchased${pendingContributionCount ? ` • ${pendingContributionCount} contribution request${pendingContributionCount === 1 ? '' : 's'}` : ''}`
                : `${draftCount} draft • ${finalizedCount} live`}
            </p>
          </div>
          <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end" data-header-action-group>
            {canManageWishlist && (
              <>
                <Button
                  variant="outline"
                  onClick={handleFinalizeAllDrafts}
                  disabled={finalizingAll || draftCount === 0}
                  data-header-action
                  data-testid="finalize-all-gifts-btn"
                >
                  <LockSimple className="w-4 h-4 mr-2" />
                  {finalizingAll ? 'Finalizing...' : 'Finalize Drafts'}
                </Button>
                <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-header-action data-testid="bulk-upload-gifts-btn">
                      <Upload className="w-4 h-4 mr-2" /> Bulk Upload
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Bulk Upload Gifts</DialogTitle>
                      <DialogDescription>Upload an Excel file with columns: name, description, price, link.</DialogDescription>
                    </DialogHeader>
                    <Input type="file" accept=".xlsx,.xls" onChange={handleBulkUpload} data-testid="bulk-gifts-input" />
                  </DialogContent>
                </Dialog>
              </>
            )}

            {(canManageWishlist || canUpdateGiftStatus) && (
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-wedding-sage hover:bg-wedding-sage/90" data-header-action data-testid="add-gift-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {canManageWishlist ? 'Add Gift' : 'Suggest a Gift'}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{canManageWishlist ? 'Add Gift to Registry' : 'Suggest a Gift'}</DialogTitle>
                    <DialogDescription>
                      {canManageWishlist
                        ? 'Add a draft gift, then finalize it when it is ready for guests.'
                        : 'Add an idea directly to the wishlist so other guests can reserve or contribute to it.'}
                    </DialogDescription>
                  </DialogHeader>
                  <GiftForm
                    gift={newGift}
                    setGift={setNewGift}
                    onSubmit={handleAddGift}
                    submitLabel={canManageWishlist ? 'Add Gift' : 'Send Idea'}
                    testIdPrefix="gift"
                  />
                </DialogContent>
              </Dialog>
            )}
          </div>
        </StickyActionHeader>

        {canManageWishlist && (
          <EventScheduledReminderCard
            reminderKey="wishlist"
            title="Wishlist reminders"
            description="Schedule WhatsApp reminders for guests before the event."
            compact
          />
        )}

        {(canManageWishlist || deliveryAddress.configured) && (
          <Card>
            <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex gap-3">
                <div className="rounded-full bg-wedding-sage/10 p-2 text-wedding-sage">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-medium">Send gifts directly to {eventTypeConfig.labels.registryRecipient}</h2>
                  {deliveryAddress.configured ? (
                    <div className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{addressText}</div>
                  ) : (
                    <p className="mt-1 text-sm text-muted-foreground">
                      Add a delivery address for guests who cannot attend in person.
                    </p>
                  )}
                  {canManageWishlist && deliveryAddress.configured && (
                    <Badge className={`mt-2 ${deliveryAddress.is_visible_to_guests ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>
                      {deliveryAddress.is_visible_to_guests ? 'Visible to guests' : 'Hidden from guests'}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                {deliveryAddress.configured && (
                  <Button variant="outline" size="sm" onClick={handleCopyAddress}>
                    <Copy className="mr-1 h-4 w-4" /> Copy
                  </Button>
                )}
                {canManageWishlist && (
                  <Button variant="outline" size="sm" onClick={() => setShowAddressDialog(true)}>
                    <PencilSimple className="mr-1 h-4 w-4" />
                    {deliveryAddress.configured ? 'Edit' : 'Add Address'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {canManageWishlist && giftReveal.event_ended && (
          <Card data-testid="gift-reveal">
            <CardContent className="p-0">
              <div className="flex items-start gap-3 border-b p-5">
                <div className="rounded-md bg-wedding-gold/20 p-2 text-wedding-charcoal">
                  <GiftIcon className="h-5 w-5" weight="duotone" />
                </div>
                <div className="flex items-center gap-1">
                  <h2 className="font-serif text-xl font-semibold text-wedding-charcoal">Who gifted what</h2>
                  <InfoTip content="After the event ends, guest names are revealed for purchased gifts." label="About gift name reveal" />
                </div>
              </div>
              {giftReveal.gifts.length > 0 ? (
                <div className="divide-y">
                  {giftReveal.gifts.map((gift) => (
                    <div key={gift.gift_id} className="grid gap-1 px-5 py-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] sm:gap-6">
                      <p className="font-medium text-wedding-charcoal">{gift.gift_name}</p>
                      <p className="text-sm text-muted-foreground">
                        Gifted by <span className="font-medium text-foreground">{gift.guest_names.join(', ') || 'Guest name unavailable'}</span>
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="px-5 py-8 text-center text-sm text-muted-foreground">No gifts were marked as purchased.</p>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Gift</TableHead>
                  {showPrice && <TableHead className="hidden sm:table-cell">Price</TableHead>}
                  <TableHead className="hidden md:table-cell">Link</TableHead>
                  <TableHead>{canManageWishlist ? 'Stage' : 'Status'}</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayGifts.map((gift) => {
                  const status = statusConfig[gift.status] || statusConfig.available;
                  const isSuggestion = Boolean(gift.suggested_by);
                  return (
                    <TableRow key={gift.id} data-testid={`gift-${gift.id}`}>
                      <TableCell>
                        <div className="min-w-36 max-w-72">
                          <p className="font-medium">{gift.name}</p>
                          {gift.description && <p className="text-xs text-muted-foreground">{gift.description}</p>}
                          {canManageWishlist && isSuggestion && (
                            <p className="mt-1 text-xs text-wedding-sage">
                              Suggested by {gift.suggested_by_name || 'a guest'}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      {showPrice && (
                        <TableCell className="hidden sm:table-cell">
                          {gift.price != null ? `₹${Number(gift.price).toFixed(2)}` : '-'}
                        </TableCell>
                      )}
                      <TableCell className="hidden md:table-cell">
                        {gift.link ? (
                          <a href={gift.link} target="_blank" rel="noopener noreferrer" className="inline-flex text-muted-foreground hover:text-wedding-sage" aria-label={`Open purchase link for ${gift.name}`}>
                            <LinkSimple className="w-5 h-5" />
                          </a>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {canManageWishlist ? (
                          <ManagerStage gift={gift} />
                        ) : gift.is_finalized ? (
                          <Badge className={status.color}>{status.label}</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">Draft</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {canUpdateGiftStatus && renderGuestActions(gift)}
                        {canManageWishlist && !gift.is_finalized && (
                          <div className="flex min-w-52 flex-wrap gap-2">
                            <Button variant="outline" size="sm" onClick={() => openEditGift(gift)} data-testid={`edit-${gift.id}`}>
                              <PencilSimple className="w-4 h-4 mr-1" /> Edit
                            </Button>
                            <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => handleDeleteGift(gift.id)} data-testid={`delete-${gift.id}`}>
                              <Trash className="w-4 h-4 mr-1" /> Delete
                            </Button>
                            <Button variant="outline" size="sm" className="border-wedding-sage text-wedding-sage" onClick={() => handleFinalizeGift(gift)} data-testid={`finalize-${gift.id}`}>
                              <LockSimple className="w-4 h-4 mr-1" /> Finalize
                            </Button>
                          </div>
                        )}
                        {canManageWishlist && gift.is_finalized && (
                          <span className="text-xs text-muted-foreground">Finalized gifts are locked.</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {visibleGifts.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={showPrice ? 5 : 4} className="py-8 text-center text-muted-foreground">
                      No gifts in the registry yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Gift</DialogTitle>
              <DialogDescription>Update this gift before it is finalized.</DialogDescription>
            </DialogHeader>
            {editingGift && (
              <GiftForm gift={editingGift} setGift={setEditingGift} onSubmit={handleEditGift} submitLabel="Update Gift" testIdPrefix="edit-gift" />
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Gift Delivery Address</DialogTitle>
              <DialogDescription>
                Guests will only see this address when “Show this address to guests” is selected.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveAddress} className="space-y-4">
              <div>
                <Label htmlFor="recipient-name">Recipient name *</Label>
                <Input id="recipient-name" required value={addressForm.recipient_name} onChange={(event) => setAddressForm({ ...addressForm, recipient_name: event.target.value })} />
              </div>
              <div>
                <Label htmlFor="address-line-1">Address line 1 *</Label>
                <Input id="address-line-1" required value={addressForm.address_line1} onChange={(event) => setAddressForm({ ...addressForm, address_line1: event.target.value })} />
              </div>
              <div>
                <Label htmlFor="address-line-2">Address line 2</Label>
                <Input id="address-line-2" value={addressForm.address_line2} onChange={(event) => setAddressForm({ ...addressForm, address_line2: event.target.value })} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="address-city">City *</Label>
                  <Input id="address-city" required value={addressForm.city} onChange={(event) => setAddressForm({ ...addressForm, city: event.target.value })} />
                </div>
                <div>
                  <Label htmlFor="address-state">State *</Label>
                  <Input id="address-state" required value={addressForm.state} onChange={(event) => setAddressForm({ ...addressForm, state: event.target.value })} />
                </div>
                <div>
                  <Label htmlFor="address-postal-code">Postal code *</Label>
                  <Input id="address-postal-code" required value={addressForm.postal_code} onChange={(event) => setAddressForm({ ...addressForm, postal_code: event.target.value })} />
                </div>
                <div>
                  <Label htmlFor="address-country">Country *</Label>
                  <Input id="address-country" required value={addressForm.country} onChange={(event) => setAddressForm({ ...addressForm, country: event.target.value })} />
                </div>
              </div>
              <div>
                <Label htmlFor="delivery-notes">Delivery notes</Label>
                <Textarea id="delivery-notes" rows={2} placeholder="Landmark, preferred delivery hours, or other useful details" value={addressForm.delivery_notes} onChange={(event) => setAddressForm({ ...addressForm, delivery_notes: event.target.value })} />
              </div>
              <div className="flex items-start gap-3 rounded-md border p-3">
                <Checkbox id="show-address" checked={addressForm.is_visible_to_guests} onCheckedChange={(checked) => setAddressForm({ ...addressForm, is_visible_to_guests: Boolean(checked) })} />
                <div>
                  <Label htmlFor="show-address" className="cursor-pointer">Show this address to guests</Label>
                  <p className="text-xs text-muted-foreground">Enable this only when the address is complete and ready to share.</p>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={savingAddress} className="bg-wedding-sage hover:bg-wedding-sage/90">
                  {savingAddress ? 'Saving...' : 'Save Address'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(contributionGift)} onOpenChange={(open) => !open && setContributionGift(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ask to Contribute</DialogTitle>
              <DialogDescription>
                Ask the guest handling “{contributionGift?.name}” if you can share the cost. Contact details are shared only if they accept.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleRequestContribution} className="space-y-4">
              <div>
                <Label htmlFor="contribution-amount">Amount you can contribute (optional)</Label>
                <Input id="contribution-amount" type="number" min="0.01" step="0.01" value={contribution.amount} onChange={(event) => setContribution({ ...contribution, amount: event.target.value })} />
              </div>
              <div>
                <Label htmlFor="contribution-message">Message (optional)</Label>
                <Textarea id="contribution-message" maxLength={300} rows={3} placeholder="For example: I would love to split this gift with you." value={contribution.message} onChange={(event) => setContribution({ ...contribution, message: event.target.value })} />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-wedding-sage hover:bg-wedding-sage/90">Send Request</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function GiftForm({ gift, setGift, onSubmit, submitLabel, testIdPrefix }) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label htmlFor={`${testIdPrefix}-name`}>Gift Name *</Label>
        <Input id={`${testIdPrefix}-name`} value={gift.name} onChange={(event) => setGift({ ...gift, name: event.target.value })} required data-testid={`${testIdPrefix}-name-input`} />
      </div>
      <div>
        <Label htmlFor={`${testIdPrefix}-description`}>Description</Label>
        <Textarea id={`${testIdPrefix}-description`} value={gift.description} onChange={(event) => setGift({ ...gift, description: event.target.value })} rows={2} data-testid={`${testIdPrefix}-description-input`} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor={`${testIdPrefix}-price`}>Estimated Price (INR)</Label>
          <Input id={`${testIdPrefix}-price`} type="number" min="0" step="0.01" value={gift.price} onChange={(event) => setGift({ ...gift, price: event.target.value })} data-testid={`${testIdPrefix}-price-input`} />
        </div>
        <div>
          <Label htmlFor={`${testIdPrefix}-link`}>Purchase Link</Label>
          <Input id={`${testIdPrefix}-link`} type="url" value={gift.link} onChange={(event) => setGift({ ...gift, link: event.target.value })} data-testid={`${testIdPrefix}-link-input`} />
        </div>
      </div>
      <DialogFooter>
        <Button type="submit" className="bg-wedding-sage hover:bg-wedding-sage/90" data-testid={`${testIdPrefix}-save-btn`}>
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}

function ManagerStage({ gift }) {
  if (gift.is_finalized && gift.suggested_by) {
    return <Badge className="bg-blue-100 text-blue-700">Guest added</Badge>;
  }
  if (gift.is_finalized) {
    return <Badge className="bg-wedding-sage text-white">Finalized</Badge>;
  }
  return <Badge className="bg-amber-100 text-amber-700">Draft</Badge>;
}
